// Lo que el plan de la cuenta cambia en el editor (docs/PLANES.md):
// - Archivo → «Compartir…»: quién puede abrir el proyecto (cualquiera con el enlace o solo yo; privado es de Maker) y
//   su enlace. Un proyecto propio privado lleva «Privado» con candado junto a su nombre y en el menú Archivo.
// - El enlace de un proyecto que no se puede abrir: no existe o es privado de alguien más (el servidor responde 404
//   a los dos).
// - El tope de proyectos del plan (409 al guardar uno nuevo o una copia).
// - El tope de compilaciones (429): no es un error del sketch. Las que quedan hoy se ven bajo el código (panels.js).
import { escapeHtml } from './html.js';
import { openModal, openProjects } from './dialogs.js';
import { isTaskMode } from './modes.js';
import { loginUrl, registerUrl } from '../session.js';

export function setupPlan(app) {
  const badge = document.getElementById('vis-badge');
  const hint = document.querySelector('.menu-list [data-action="link"] .menu-hint');
  const sync = () => {
    const priv = app.kind === 'project' && !!app.projectId && app.visibility === 'private';
    badge.hidden = !priv;
    hint.hidden = !priv;
  };
  // save-state también: al borrar el proyecto abierto desde «Mis proyectos» queda sin guardar.
  for (const ev of ['mode', 'project-loaded', 'visibility', 'save-state']) app.on(ev, sync);
  sync();

  app.on('project-unavailable', (id) => projectUnavailable(app, id));
  app.on('project-limit', (message) => projectLimit(app, message));
  app.on('compile-limit', (message) => compileLimit(app, message));
}

// Compartir -----------------------------------------------------------------------------------------------------

/** Archivo → «Compartir…» en un proyecto propio; en uno ajeno, «Copiar enlace». */
export async function openShare(app) {
  if (app.kind === 'foreign') return copyLink(app, location.href, `Enlace copiado: ${location.href}`);
  // Se comparte lo que se ve: antes se guarda (sin cuenta, saveNow pide entrar).
  if (!app.projectId || app.dirty) await app.saveNow();
  if (!app.projectId || app.kind !== 'project') return;
  const body = openModal(`Compartir «${app.project.name}»`, '<div class="share"><div class="empty">Cargando…</div></div>', { narrow: true });
  const box = body.firstElementChild; // si se cierra mientras carga, queda suelto y no pisa otra ventana
  // El plan pudo cambiar desde que se abrió la página (lo asignó el administrador o venció).
  await app.refreshPlan().catch(() => {});
  renderShare(app, box);
  // Con el teclado se sigue en la ventana: el foco va a la opción elegida.
  box.querySelector('input[name=vis]:checked')?.focus();
}

function renderShare(app, box) {
  const url = `${location.origin}/p/${app.projectId}`;
  box.innerHTML = `<fieldset class="vis-options">
      <legend>Quién puede abrirlo</legend>
      <label class="vis-option"><input type="radio" name="vis" value="link">
        <span><b>Cualquiera con el enlace</b><small>Lo ve en solo lectura y puede guardar una copia.</small></span></label>
      <label class="vis-option"><input type="radio" name="vis" value="private">
        <span><b>Solo yo</b><small>Nadie más lo abre, ni con el enlace.</small>
          <small class="vis-why" id="vis-why">Los proyectos privados son del plan Maker.</small></span></label>
    </fieldset>
    <p class="muted small" id="vis-keep">Si lo cambias a «Cualquiera con el enlace», ya no podrás volver a hacerlo privado: los proyectos privados son del plan Maker.</p>
    <p class="form-error notice error" role="alert"></p>
    <div class="field share-field"><label for="share-url">Enlace</label>
      <div class="share-link"><input class="input mono" id="share-url" type="text" readonly value="${escapeHtml(url)}">
        <button class="btn primary" type="button" data-copy>Copiar enlace</button></div>
      <span class="help" id="share-link-note">Quien lo abra verá el proyecto en solo lectura y podrá guardar una copia.</span>
      <p class="notice warn" id="share-private-note">Es privado: solo tú lo abres, con tu cuenta. Si compartes este enlace, nadie más podrá abrirlo.</p></div>`;
  const radios = [...box.querySelectorAll('input[name=vis]')];
  const priv = radios[1];
  const err = box.querySelector('.form-error');
  const update = () => {
    const isPrivate = app.visibility === 'private';
    const canPrivate = !!(app.plan && app.plan.privateProjects);
    radios.forEach((r) => (r.checked = r.value === app.visibility));
    // Sin Maker no se vuelve privado uno con enlace; uno que ya es privado sigue así (docs/PLANES.md).
    priv.disabled = !canPrivate && !isPrivate;
    priv.closest('.vis-option').classList.toggle('off', priv.disabled);
    if (priv.disabled) priv.setAttribute('aria-describedby', 'vis-why');
    else priv.removeAttribute('aria-describedby');
    box.querySelector('#vis-why').hidden = !priv.disabled;
    box.querySelector('#vis-keep').hidden = !(isPrivate && !canPrivate);
    box.querySelector('#share-link-note').hidden = isPrivate;
    box.querySelector('#share-private-note').hidden = !isPrivate;
  };
  update();
  for (const r of radios) {
    r.addEventListener('change', async () => {
      err.textContent = '';
      const value = r.value;
      radios.forEach((x) => (x.disabled = true));
      try {
        await app.setVisibility(value);
        app.toast(value === 'private' ? 'Ahora es privado: solo tú lo abres.' : 'Ahora cualquiera con el enlace puede abrirlo.');
      } catch (e) {
        err.textContent = e.message;
        // 403: el plan ya no lo permite (venció Maker); se vuelve a leer para mostrarlo bien.
        if (e.status === 403) await app.refreshPlan().catch(() => {});
      }
      radios.forEach((x) => (x.disabled = false));
      update();
      box.querySelector(`input[value="${app.visibility}"]`).focus();
    });
  }
  box.querySelector('[data-copy]').addEventListener('click', () =>
    copyLink(app, url, app.visibility === 'private'
      ? 'Enlace copiado. Es privado: solo tú lo abres, con tu cuenta.'
      : 'Enlace copiado. Quien lo abra verá el proyecto en solo lectura y podrá guardar una copia.', box.querySelector('#share-url')));
}

/** Copia el enlace; sin permiso para el portapapeles, lo deja seleccionado (input) o lo muestra. */
async function copyLink(app, url, done, input = null) {
  try {
    await navigator.clipboard.writeText(url);
    app.toast(done);
  } catch {
    if (!input) return app.toast(url);
    input.focus();
    input.select();
    app.toast('Copia el enlace seleccionado con Ctrl+C.');
  }
}

// Avisos ----------------------------------------------------------------------------------------------------------

function projectUnavailable(app, id) {
  const u = app.user;
  openModal(
    'No se puede abrir este proyecto',
    `<p style="margin-top:0">No encontramos el proyecto de este enlace. Puede que su dueño lo haya borrado o que lo haya hecho privado: un proyecto privado solo lo abre su dueño.</p>
     <p class="muted">${u ? `Si es tuyo, revisa que entraste con la cuenta correcta (estás como ${escapeHtml(u.name)}).` : 'Si es tuyo, entra con tu cuenta para abrirlo.'}</p>
     <div class="modal-actions"><button class="btn${u ? ' primary' : ''}" data-close>${u ? 'Entendido' : 'Cerrar'}</button>
       ${u ? '' : `<a class="btn primary" href="${escapeHtml(loginUrl('/p/' + id))}" data-leave data-nosave>Entrar</a>`}</div>`,
    { narrow: true },
  );
}

/** 409 al guardar un proyecto nuevo o una copia: ya tiene los proyectos que guarda su plan. */
function projectLimit(app, message) {
  const inTask = isTaskMode(app);
  const body = openModal(
    'No se pudo guardar',
    `<p style="margin-top:0">${escapeHtml(message)}</p>
     <p class="muted">${inTask ? 'Tu trabajo en esta tarea no cambia.' : 'Lo que tienes abierto sigue aquí, sin guardar.'}</p>
     <div class="modal-actions"><button class="btn" data-close>Cerrar</button>
       ${inTask ? '' : '<button class="btn primary" data-projects>Abrir Mis proyectos</button>'}</div>`,
    { narrow: true },
  );
  body.querySelector('[data-projects]')?.addEventListener('click', () => openProjects(app));
}

/** 429 al compilar: el mensaje del servidor dice cuánto esperar (y, en el tope diario, qué plan no tiene tope). */
function compileLimit(app, message) {
  const back = location.pathname + location.search;
  openModal(
    'Tope de compilaciones',
    `<p style="margin-top:0">${escapeHtml(message)}</p>
     <p class="muted">No es un error de tu código. Mientras tanto puedes seguir editando${app.user ? ' y guardando' : ''}.</p>
     <div class="modal-actions">${app.user
       ? '<button class="btn primary" data-close>Entendido</button>'
       : `<button class="btn" data-close>Cerrar</button><a class="btn" href="${escapeHtml(registerUrl(back))}" data-leave data-nosave>Crear cuenta</a>
         <a class="btn primary" href="${escapeHtml(loginUrl(back))}" data-leave data-nosave>Entrar</a>`}</div>`,
    { narrow: true },
  );
}
